/**
 * /admin/rewards — Rewards Phase D admin dashboard.
 *
 * Four sections, per spec:
 *  (a) User lookup + ledger viewer
 *  (b) Surprise Bonus tool
 *  (c) Manual Points Adjustment tool
 *  (d) Redemption usage log (global)
 *
 * Traveler surface is NEVER exposed to this route — the outer
 * `<AdminOnly>` guard in App.js and the AdminTopNav visibility rule
 * enforce it.  All API calls hit `atlas_admin`-guarded endpoints
 * (`/api/rewards/v2/admin/...`).
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Search, Sparkles, MinusCircle, PlusCircle, RefreshCw,
  UserRound, Coins, Clock, ListRestart,
} from "lucide-react";

const TESTID = {
  page:                 "admin-rewards-page",
  userSearchInput:      "admin-rewards-user-search-input",
  userSearchBtn:        "admin-rewards-user-search-btn",
  userResultsList:      "admin-rewards-user-results",
  userResult:           (id) => `admin-rewards-user-result-${id}`,
  profileCard:          "admin-rewards-profile-card",
  ledgerTable:          "admin-rewards-ledger-table",
  ledgerTypeFilter:     "admin-rewards-ledger-type-filter",
  surpriseForm:         "admin-rewards-surprise-form",
  surpriseUserId:       "admin-rewards-surprise-user-id",
  surprisePoints:       "admin-rewards-surprise-points",
  surpriseReason:       "admin-rewards-surprise-reason",
  surpriseNote:         "admin-rewards-surprise-note",
  surpriseSubmit:       "admin-rewards-surprise-submit",
  adjustForm:           "admin-rewards-adjust-form",
  adjustUserId:         "admin-rewards-adjust-user-id",
  adjustDelta:          "admin-rewards-adjust-delta",
  adjustReason:         "admin-rewards-adjust-reason",
  adjustNote:           "admin-rewards-adjust-note",
  adjustSubmit:         "admin-rewards-adjust-submit",
  redemptionsTable:     "admin-rewards-redemptions-table",
  redemptionsStatus:    "admin-rewards-redemptions-status-filter",
  redemptionsProvider:  "admin-rewards-redemptions-provider-filter",
  redemptionsRefresh:   "admin-rewards-redemptions-refresh",
  sweeperRun:           "admin-rewards-sweeper-run",
};

function fmtInt(n) {
  return (Number(n) || 0).toLocaleString();
}

function fmtUsd(n) {
  return (Number(n) || 0).toLocaleString(undefined, {
    style: "currency", currency: "USD",
  });
}

function truncMiddle(s, keep = 6) {
  if (!s) return "";
  if (s.length <= keep * 2 + 3) return s;
  return `${s.slice(0, keep)}…${s.slice(-keep)}`;
}

/* ═══════════════════════════════════════════════════════════
 * Section (a) — user lookup + ledger viewer
 * ═══════════════════════════════════════════════════════ */
function UserLookupSection({ selectedUserId, onSelectUser }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");

  const search = async () => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get("/rewards/v2/admin/users/search", {
        params: { q: q.trim(), limit: 20 },
      });
      setResults(data.users || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const pickUser = async (u) => {
    onSelectUser(u.id);
    setProfile(null);
    setLedger([]);
    try {
      const [{ data: pd }, { data: ld }] = await Promise.all([
        api.get(`/rewards/v2/admin/profile/${encodeURIComponent(u.id)}`),
        api.get("/rewards/v2/admin/transactions", {
          params: { user_id: u.id, limit: 200,
                    type_filter: typeFilter || undefined },
        }),
      ]);
      setProfile(pd.profile);
      setLedger(ld.transactions || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    }
  };

  const refetchLedger = async () => {
    if (!selectedUserId) return;
    try {
      const { data } = await api.get("/rewards/v2/admin/transactions", {
        params: { user_id: selectedUserId, limit: 200,
                  type_filter: typeFilter || undefined },
      });
      setLedger(data.transactions || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reload failed");
    }
  };

  useEffect(() => {
    if (selectedUserId) refetchLedger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter]);

  return (
    <section className="rounded-xl border border-neutral-200 bg-white p-4 space-y-4">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <UserRound className="w-4 h-4" /> User lookup + ledger
      </h2>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            data-testid={TESTID.userSearchInput}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") search(); }}
            placeholder="Search by email or user_id"
            className="w-full h-9 pl-8 pr-3 rounded-md border border-neutral-300 outline-none focus:border-tb-navy text-sm"
          />
        </div>
        <button
          data-testid={TESTID.userSearchBtn}
          onClick={search}
          disabled={loading}
          className="h-9 px-4 rounded-md bg-tb-navy text-white text-sm hover:brightness-110 disabled:opacity-60"
        >
          {loading ? "…" : "Search"}
        </button>
      </div>

      {results.length > 0 && (
        <ul
          data-testid={TESTID.userResultsList}
          className="border border-neutral-200 rounded-md divide-y divide-neutral-100 max-h-56 overflow-auto"
        >
          {results.map((u) => (
            <li key={u.id}>
              <button
                data-testid={TESTID.userResult(u.id)}
                onClick={() => pickUser(u)}
                className={
                  "w-full text-left px-3 py-2 text-sm hover:bg-neutral-50 " +
                  (selectedUserId === u.id ? "bg-neutral-100" : "")
                }
              >
                <div className="font-medium">{u.email}</div>
                <div className="text-xs text-neutral-500 flex items-center gap-2">
                  <span>{truncMiddle(u.id)}</span>
                  {u.rewards_profile_summary?.current_tier && (
                    <span className="px-1.5 py-0.5 rounded bg-neutral-100 border border-neutral-200">
                      {u.rewards_profile_summary.current_tier}
                    </span>
                  )}
                  {u.rewards_profile_summary && (
                    <span>
                      avail={fmtInt(u.rewards_profile_summary.redeemable_points)} ·
                      lifetime={fmtInt(u.rewards_profile_summary.lifetime_points)}
                    </span>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {profile && (
        <div
          data-testid={TESTID.profileCard}
          className="rounded-md border border-neutral-200 bg-neutral-50 p-3 grid grid-cols-2 md:grid-cols-6 gap-3 text-sm"
        >
          <Stat label="Tier" value={profile.current_tier} />
          <Stat label="Reward Points" value={fmtInt(profile.redeemable_points)} />
          <Stat label="Reserved" value={fmtInt(profile.reserved_points)} />
          <Stat label="Pending" value={fmtInt(profile.pending_points)} />
          <Stat label="Lifetime" value={fmtInt(profile.lifetime_points)} />
          <Stat label="Redeemed" value={fmtInt(profile.redeemed_points)} />
          <Stat
            label="Credit USD"
            value={fmtUsd(profile.redeemable_credit_usd)}
            span="md:col-span-2"
          />
          <Stat
            label="Rate"
            value={`${profile.conversion_rate} pts / $1`}
            span="md:col-span-2"
          />
        </div>
      )}

      {profile && (
        <>
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">
              Ledger — {fmtInt(ledger.length)} rows (newest first)
            </div>
            <input
              data-testid={TESTID.ledgerTypeFilter}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              placeholder="filter types (comma-sep, e.g. surprise_bonus,redemption_committed)"
              className="h-8 px-2 rounded border border-neutral-300 text-xs w-96 max-w-full"
            />
          </div>
          <div className="overflow-auto rounded-md border border-neutral-200 max-h-[420px]">
            <table
              data-testid={TESTID.ledgerTable}
              className="w-full text-xs"
            >
              <thead className="bg-neutral-50 text-neutral-600">
                <tr>
                  <Th>When</Th>
                  <Th>Type</Th>
                  <Th className="text-right">Δ avail</Th>
                  <Th className="text-right">Δ reserved</Th>
                  <Th className="text-right">Δ lifetime</Th>
                  <Th className="text-right">Δ redeemed</Th>
                  <Th className="text-right">$</Th>
                  <Th>Description</Th>
                </tr>
              </thead>
              <tbody>
                {ledger.map((r) => (
                  <tr key={r.transaction_id} className="border-t border-neutral-100">
                    <Td>{r.created_at?.slice(0, 19).replace("T", " ")}</Td>
                    <Td className="font-mono">{r.type}</Td>
                    <Td className="text-right">{r.points_delta_redeemable}</Td>
                    <Td className="text-right">{r.points_delta_reserved || 0}</Td>
                    <Td className="text-right">{r.points_delta_lifetime}</Td>
                    <Td className="text-right">{r.points_delta_redeemed}</Td>
                    <Td className="text-right">
                      {r.dollar_value != null ? fmtUsd(r.dollar_value) : "—"}
                    </Td>
                    <Td className="text-neutral-700">{r.description}</Td>
                  </tr>
                ))}
                {ledger.length === 0 && (
                  <tr>
                    <Td colSpan={8} className="text-center text-neutral-500 py-4">
                      no ledger rows
                    </Td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
 * Section (b) — Surprise Bonus tool
 * ═══════════════════════════════════════════════════════ */
function SurpriseBonusSection({ prefUserId, onDone }) {
  const [userId, setUserId] = useState(prefUserId || "");
  const [points, setPoints] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (prefUserId) setUserId(prefUserId); }, [prefUserId]);

  const submit = async (e) => {
    e.preventDefault();
    const pts = Number(points);
    if (!userId.trim()) return toast.error("user_id required");
    if (!Number.isInteger(pts) || pts <= 0) return toast.error("points must be a positive integer");
    if (!reason.trim()) return toast.error("reason required");
    if (!window.confirm(
      `Grant ${pts.toLocaleString()} surprise points to ${userId}?\n\nReason: ${reason}`
    )) return;
    setSubmitting(true);
    try {
      await api.post("/rewards/v2/admin/surprise-bonus", {
        user_id: userId.trim(),
        points: pts,
        reason: reason.trim(),
        note: note.trim() || null,
      });
      toast.success(`+${pts.toLocaleString()} pts awarded`);
      setPoints(""); setReason(""); setNote("");
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="rounded-xl border border-neutral-200 bg-white p-4 space-y-3">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-tb-gold-bright" /> Surprise bonus
      </h2>
      <form data-testid={TESTID.surpriseForm} onSubmit={submit} className="space-y-2">
        <Field label="User ID">
          <input
            data-testid={TESTID.surpriseUserId}
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder="pick from user search or paste id"
            className="input"
          />
        </Field>
        <Field label="Points (positive int)">
          <input
            data-testid={TESTID.surprisePoints}
            type="number" min={1} step={1}
            value={points}
            onChange={(e) => setPoints(e.target.value)}
            placeholder="e.g. 500"
            className="input"
          />
        </Field>
        <Field label="Reason (required)">
          <input
            data-testid={TESTID.surpriseReason}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="birthday, launch bonus, apology, …"
            className="input"
          />
        </Field>
        <Field label="Note (optional)">
          <input
            data-testid={TESTID.surpriseNote}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="internal-only context"
            className="input"
          />
        </Field>
        <button
          data-testid={TESTID.surpriseSubmit}
          type="submit"
          disabled={submitting}
          className="w-full h-9 rounded-md bg-tb-gold text-tb-navy font-semibold hover:brightness-105 disabled:opacity-60"
        >
          <span className="inline-flex items-center gap-1.5">
            <PlusCircle className="w-4 h-4" /> Award surprise bonus
          </span>
        </button>
      </form>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
 * Section (c) — Manual Points Adjustment tool
 * ═══════════════════════════════════════════════════════ */
function ManualAdjustSection({ prefUserId, onDone }) {
  const [userId, setUserId] = useState(prefUserId || "");
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (prefUserId) setUserId(prefUserId); }, [prefUserId]);

  const submit = async (e) => {
    e.preventDefault();
    const d = Number(delta);
    if (!userId.trim()) return toast.error("user_id required");
    if (!Number.isInteger(d) || d === 0) return toast.error("delta must be a non-zero integer");
    if (!reason.trim()) return toast.error("reason required");
    const verb = d > 0 ? "credit" : "debit";
    if (!window.confirm(
      `${verb === "credit" ? "Credit" : "Debit"} ${Math.abs(d).toLocaleString()} pts ` +
      `${verb === "credit" ? "to" : "from"} ${userId}?\n\n` +
      (d < 0 ? "Lifetime points will be PRESERVED (tier not lowered).\n\n" : "") +
      `Reason: ${reason}`
    )) return;
    setSubmitting(true);
    try {
      await api.post("/rewards/v2/admin/points-adjustment", {
        user_id: userId.trim(),
        points_delta_redeemable: d,
        reason: reason.trim(),
        note: note.trim() || null,
      });
      toast.success(`${d > 0 ? "+" : ""}${d.toLocaleString()} pts applied`);
      setDelta(""); setReason(""); setNote("");
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="rounded-xl border border-neutral-200 bg-white p-4 space-y-3">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <MinusCircle className="w-4 h-4 text-tb-coral" /> Manual points adjustment
        <span className="text-xs font-normal text-neutral-500">
          (signed · debit preserves lifetime)
        </span>
      </h2>
      <form data-testid={TESTID.adjustForm} onSubmit={submit} className="space-y-2">
        <Field label="User ID">
          <input
            data-testid={TESTID.adjustUserId}
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder="pick from user search or paste id"
            className="input"
          />
        </Field>
        <Field label="Δ Reward Points (signed int, e.g. -250 or +100)">
          <input
            data-testid={TESTID.adjustDelta}
            type="number" step={1}
            value={delta}
            onChange={(e) => setDelta(e.target.value)}
            placeholder="e.g. -250"
            className="input"
          />
        </Field>
        <Field label="Reason (required)">
          <input
            data-testid={TESTID.adjustReason}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="chargeback, correction, fraud reversal, …"
            className="input"
          />
        </Field>
        <Field label="Note (optional)">
          <input
            data-testid={TESTID.adjustNote}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="internal-only context"
            className="input"
          />
        </Field>
        <button
          data-testid={TESTID.adjustSubmit}
          type="submit"
          disabled={submitting}
          className="w-full h-9 rounded-md bg-tb-navy text-white font-semibold hover:brightness-110 disabled:opacity-60"
        >
          <span className="inline-flex items-center gap-1.5">
            <Coins className="w-4 h-4" /> Apply adjustment
          </span>
        </button>
      </form>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
 * Section (d) — Redemption usage log (global)
 * ═══════════════════════════════════════════════════════ */
function RedemptionsSection() {
  const [status, setStatus] = useState("");
  const [provider, setProvider] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allowed, setAllowed] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rewards/v2/admin/redemptions", {
        params: {
          status:   status || undefined,
          provider: provider || undefined,
          limit:    200,
        },
      });
      setRows(data.redemptions || []);
      setAllowed(data.allowed_statuses || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runSweeper = async () => {
    try {
      const { data } = await api.post("/rewards/v2/admin/sweeper/run-once");
      toast.success(`Sweeper released ${data.released} expired reservation(s)`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sweep failed");
    }
  };

  return (
    <section className="rounded-xl border border-neutral-200 bg-white p-4 space-y-3">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <Clock className="w-4 h-4" /> Redemption usage log
      </h2>
      <div className="flex flex-wrap gap-2 items-center">
        <select
          data-testid={TESTID.redemptionsStatus}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-8 rounded border border-neutral-300 text-xs px-2"
        >
          <option value="">any status</option>
          {allowed.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <input
          data-testid={TESTID.redemptionsProvider}
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
          placeholder="provider filter (e.g. stub, amadeus)"
          className="h-8 rounded border border-neutral-300 text-xs px-2 w-64"
        />
        <button
          data-testid={TESTID.redemptionsRefresh}
          onClick={load}
          disabled={loading}
          className="h-8 px-3 rounded-md bg-tb-navy text-white text-xs hover:brightness-110 inline-flex items-center gap-1"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
        <button
          data-testid={TESTID.sweeperRun}
          onClick={runSweeper}
          className="h-8 px-3 rounded-md bg-tb-gold text-tb-navy text-xs font-semibold inline-flex items-center gap-1 hover:brightness-105"
          title="Force-release expired reservations right now (also runs every 60s)"
        >
          <ListRestart className="w-3 h-3" /> Run sweeper
        </button>
        <span className="text-xs text-neutral-500 ml-auto">
          {fmtInt(rows.length)} rows
        </span>
      </div>
      <div className="overflow-auto rounded-md border border-neutral-200 max-h-[500px]">
        <table
          data-testid={TESTID.redemptionsTable}
          className="w-full text-xs"
        >
          <thead className="bg-neutral-50 text-neutral-600">
            <tr>
              <Th>Created</Th>
              <Th>User</Th>
              <Th>Provider</Th>
              <Th>Status</Th>
              <Th className="text-right">Pts</Th>
              <Th className="text-right">$</Th>
              <Th className="text-right">Booking $</Th>
              <Th>Booking ID</Th>
              <Th>Refund ref</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.reservation_id} className="border-t border-neutral-100">
                <Td>{r.created_at?.slice(0, 19).replace("T", " ")}</Td>
                <Td>
                  <div className="font-medium">{r.user_email || "—"}</div>
                  <div className="text-[10px] text-neutral-500 font-mono">
                    {truncMiddle(r.user_id)}
                  </div>
                </Td>
                <Td className="font-mono">{r._provider}</Td>
                <Td>
                  <span className={
                    "px-1.5 py-0.5 rounded text-[10px] border " +
                    (r.status === "committed" ? "bg-emerald-50 border-emerald-300 text-emerald-800" :
                     r.status === "reversed"  ? "bg-amber-50 border-amber-300 text-amber-800" :
                     r.status === "released"  ? "bg-neutral-100 border-neutral-300 text-neutral-600" :
                                                  "bg-blue-50 border-blue-300 text-blue-800")
                  }>{r.status}</span>
                  {r.status === "committed" && r.metadata?.pending_payout && (
                    <div className="text-[10px] text-amber-700 mt-0.5">pending payout</div>
                  )}
                </Td>
                <Td className="text-right">{fmtInt(r.points_reserved)}</Td>
                <Td className="text-right">{fmtUsd(r.dollar_value)}</Td>
                <Td className="text-right">
                  {r._booking_amount_usd != null ? fmtUsd(r._booking_amount_usd) : "—"}
                </Td>
                <Td className="font-mono">{r.booking_id || "—"}</Td>
                <Td className="font-mono text-[10px]">{r.refund_reference || "—"}</Td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <Td colSpan={9} className="text-center text-neutral-500 py-4">
                  no redemptions
                </Td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}


/* ═══════════════════════════════════════════════════════════
 * Verify External Review section — Earn Rewrite (2026-08-13)
 * ═══════════════════════════════════════════════════════ */
function VerifyReviewSection({ prefUserId, onDone }) {
  const [userId, setUserId] = React.useState(prefUserId || "");
  const [platform, setPlatform] = React.useState("google");
  const [reviewUrl, setReviewUrl] = React.useState("");
  const [note, setNote] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  React.useEffect(() => { if (prefUserId) setUserId(prefUserId); }, [prefUserId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!userId.trim()) return toast.error("user_id required");
    if (!reviewUrl.trim().match(/^https?:\/\//i))
      return toast.error("review_url must start with http(s)://");
    if (!window.confirm(
      `Verify ${platform} review and award +10 Lifetime Points to ${userId}?`
    )) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/rewards/v2/admin/verify-review", {
        user_id: userId.trim(),
        platform,
        review_url: reviewUrl.trim(),
        note: note.trim() || null,
      });
      if (data.idempotent) {
        toast(`Already verified — no new award (URL already credited).`);
      } else {
        toast.success(`+10 Lifetime Points awarded for ${platform} review`);
      }
      setReviewUrl(""); setNote("");
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="admin-rewards-verify-review-section"
      className="rounded-xl border border-neutral-200 bg-white p-4 space-y-3"
    >
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <Search className="w-4 h-4" /> Verify external review
        <span className="text-xs font-normal text-neutral-500">
          (Google · Facebook · Other · +10 Lifetime Points · admin-only)
        </span>
      </h2>
      <form
        data-testid="admin-rewards-verify-review-form"
        onSubmit={submit}
        className="grid grid-cols-1 md:grid-cols-2 gap-2"
      >
        <Field label="User ID">
          <input
            data-testid="admin-rewards-verify-review-user-id"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder="pick from user search or paste id"
            className="input"
          />
        </Field>
        <Field label="Platform">
          <select
            data-testid="admin-rewards-verify-review-platform"
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className="input"
          >
            <option value="google">Google</option>
            <option value="facebook">Facebook</option>
            <option value="other">Other</option>
          </select>
        </Field>
        <div className="md:col-span-2">
          <Field label="Review URL (must start with http(s)://)">
            <input
              data-testid="admin-rewards-verify-review-url"
              value={reviewUrl}
              onChange={(e) => setReviewUrl(e.target.value)}
              placeholder="https://g.page/r/foo/review"
              className="input"
            />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Note (optional)">
            <input
              data-testid="admin-rewards-verify-review-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="internal-only context"
              className="input"
            />
          </Field>
        </div>
        <button
          data-testid="admin-rewards-verify-review-submit"
          type="submit"
          disabled={submitting}
          className="md:col-span-2 h-9 rounded-md bg-tb-navy text-white font-semibold hover:brightness-110 disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
        >
          <Sparkles className="w-4 h-4" /> Verify review + award +10 pts
        </button>
      </form>
    </section>
  );
}

/* ─── shared primitives ─── */
function Stat({ label, value, span = "" }) {
  return (
    <div className={`flex flex-col ${span}`}>
      <span className="text-[10px] uppercase tracking-widest text-neutral-500">
        {label}
      </span>
      <span className="text-sm font-semibold">{value}</span>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <label className="block text-sm">
      <span className="text-xs text-neutral-600">{label}</span>
      {children}
    </label>
  );
}
function Th({ children, className = "" }) {
  return (
    <th className={"text-left px-2 py-1.5 font-medium " + className}>
      {children}
    </th>
  );
}
function Td({ children, className = "", colSpan }) {
  return (
    <td colSpan={colSpan} className={"px-2 py-1.5 align-top " + className}>
      {children}
    </td>
  );
}

/* ═══════════════════════════════════════════════════════════
 * Page shell
 * ═══════════════════════════════════════════════════════ */
export default function AdminRewardsPage() {
  const [selectedUserId, setSelectedUserId] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const bumpReload = () => setReloadKey((k) => k + 1);

  return (
    <div
      data-testid={TESTID.page}
      className="min-h-screen bg-neutral-50 text-tb-navy px-4 md:px-6 py-4 space-y-4"
    >
      <header className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold">Rewards admin</h1>
        <p className="text-xs text-neutral-500">
          Ledger + tools · Phase D · 10 pts = $1.00 · reservation TTL 15&nbsp;min
        </p>
      </header>

      <div key={reloadKey}>
        <UserLookupSection
          selectedUserId={selectedUserId}
          onSelectUser={setSelectedUserId}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SurpriseBonusSection prefUserId={selectedUserId} onDone={bumpReload} />
        <ManualAdjustSection  prefUserId={selectedUserId} onDone={bumpReload} />
      </div>

      <VerifyReviewSection prefUserId={selectedUserId} onDone={bumpReload} />

      <RedemptionsSection />
    </div>
  );
}

/* Inline input class utility (Tailwind arbitrary selector fallback via
   the standard "input" class isn't present in this repo; we duplicate
   the shape of other admin pages by declaring the styles inline via a
   JSX-local class chain). */
if (typeof document !== "undefined") {
  const id = "admin-rewards-inline-styles";
  if (!document.getElementById(id)) {
    const s = document.createElement("style");
    s.id = id;
    s.innerHTML = `
      [data-testid="${TESTID.page}"] .input {
        width: 100%; height: 34px; padding: 0 8px;
        border: 1px solid #d4d4d8; border-radius: 6px;
        font-size: 13px; outline: none; background: #fff;
      }
      [data-testid="${TESTID.page}"] .input:focus {
        border-color: #1a3a5f;
      }
    `;
    document.head.appendChild(s);
  }
}
